import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PeriodSelector } from './PeriodSelector';
import { PricePanel } from './PricePanel';
import { PurchaseButton } from './PurchaseButton';
import { ConfirmationSheet } from './ConfirmationSheet';
import { PurchaseConfirmationSheet } from './PurchaseConfirmationSheet';
import { InsufficientBalanceSheet } from './InsufficientBalanceSheet';
import { calculateTotalPrice } from '../utils/remnawave';
import { backButton, hapticFeedback } from "../utils/telegramSdk";
import {usePlatform} from "../hooks/usePlatform";
import {useTelegram} from "../hooks/useTelegram";


const API_URL = window.location.origin;

// Тарифы по количеству устройств
const DEVICE_TARIFFS = {
    1: 149, 2: 199, 3: 249, 4: 299, 5: 349,
    6: 399, 7: 449, 8: 499, 9: 549, 10: 599
};

// Коэффициенты для разных периодов (без скидки на 3 месяца, с 10% на 6 и 12)
const PERIOD_MULTIPLIERS = {
    1: 1,      // 1 месяц - без скидки
    3: 3,      // 3 месяца - без скидки
    6: 5.4,    // 6 месяцев - 10% скидка
    12: 10.2   // 12 месяцев - 10% скидка
};

// Функция для получения цены тарифа
const getTariffPrice = (devices, periodMonths) => {
    const basePrice = DEVICE_TARIFFS[devices] || DEVICE_TARIFFS[1];
    const multiplier = PERIOD_MULTIPLIERS[periodMonths] || 1;
    return Math.round(basePrice * multiplier);
};

// Функция для расчета обычной цены (без скидки)
const getRegularPrice = (devices, periodMonths) => {
    const basePrice = DEVICE_TARIFFS[devices] || DEVICE_TARIFFS[1];
    return basePrice * periodMonths;
};

// Функция для получения процента скидки
const getDiscountPercent = (periodMonths) => {
    if (periodMonths === 6) return 10;
    if (periodMonths === 12) return 15;
    return 0;
};

export function PurchasePage({ onBack, userId, userData, onPurchase, renewData, onGoWallet }) {
    const userPlatform = usePlatform();
    const [period, setPeriod] = useState(renewData ? 1 : 1);
    const [devices, setDevices] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showWarningSheet, setShowWarningSheet] = useState(false);
    const [showPurchaseSheet, setShowPurchaseSheet] = useState(false);
    const [showInsufficientBalanceSheet, setShowInsufficientBalanceSheet] = useState(false);
    const [activeKeysCount, setActiveKeysCount] = useState(0);
    const [isCheckingKeys, setIsCheckingKeys] = useState(false);
    const [currentBalance, setCurrentBalance] = useState(0);
    const [requiredAmount, setRequiredAmount] = useState(0);

    // Состояния для анимации счетчика
    const [displayDevices, setDisplayDevices] = useState(1);
    const [isAnimating, setIsAnimating] = useState(false);
    const [animationDirection, setAnimationDirection] = useState(null);
    const lastDeviceValue = useRef(1);

    // Состояния для плавного слайдера
    const [isDragging, setIsDragging] = useState(false);
    const [smoothDevices, setSmoothDevices] = useState(1);
    const sliderRef = useRef(null);
    const thumbRef = useRef(null);
    const fillRef = useRef(null);
    const animationRef = useRef(null);
    const animationTimeoutRef = useRef(null);

    // Константы для слайдера
    const MIN_DEVICES = 1;
    const MAX_DEVICES = 10;
    const TRACK_START = 12;
    let TRACK_END;
    if (userPlatform == 'android') {
        TRACK_END = 270;
    } else if (userPlatform == 'ios') {
        TRACK_END = 290;
    } else if (userPlatform == 'windows'){
        TRACK_END = 300;
    } else {
        TRACK_END = 310;
    }
    const SLIDER_WIDTH = TRACK_END - TRACK_START;

    // Получение баланса пользователя
    const fetchBalance = useCallback(async () => {
        if (!userId) return 0;
        try {
            const response = await fetch(`${API_URL}/api/user/${userId}/balance`);
            const data = await response.json();
            return data.balance || 0;
        } catch (error) {
            console.error('Ошибка получения баланса:', error);
            return 0;
        }
    }, [userId]);



    // Функция для проверки активных ключей
    const checkActiveKeys = useCallback(async () => {
        if (!userId) return 0;

        try {
            const response = await fetch(`${API_URL}/api/user/keys?user_id=${userId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();

            if (data.success && data.keys) {
                const now = new Date();
                const active = data.keys.filter(key => {
                    const expireAt = new Date(key.expire_at);
                    return key.status === 'ACTIVE' && expireAt > now;
                });

                return active.length;
            }
            return 0;
        } catch (error) {
            console.error('❌ Ошибка при проверке активных ключей:', error);
            return 0;
        }
    }, [userId]);

    // Функция для запуска анимации счетчика
    const triggerDeviceAnimation = useCallback((newValue) => {
        const direction = newValue > lastDeviceValue.current ? 'increase' : 'decrease';
        if (hapticFeedback.impactOccurred.isAvailable()) {
            hapticFeedback.impactOccurred('light');
        }

        if (newValue !== lastDeviceValue.current) {
            setAnimationDirection(direction);
            setIsAnimating(true);
            setDisplayDevices(newValue);

            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }

            animationTimeoutRef.current = setTimeout(() => {
                setIsAnimating(false);
                setAnimationDirection(null);
            }, 250);

            lastDeviceValue.current = newValue;
        } else {
            setDisplayDevices(newValue);
        }
    }, []);

    // Функция обновления позиции бегунка и заполняющей линии
    const updateSliderPosition = useCallback((value) => {
        if (!thumbRef.current || !sliderRef.current) return;

        const percent = (value - MIN_DEVICES) / (MAX_DEVICES - MIN_DEVICES);
        const center = TRACK_START + percent * SLIDER_WIDTH;

        thumbRef.current.style.left = `${center}px`;

        if (fillRef.current) {
            const fillWidth = Math.max(0, center - TRACK_START);
            fillRef.current.style.width = `${fillWidth}px`;
        }
    }, [MIN_DEVICES, MAX_DEVICES, TRACK_START, SLIDER_WIDTH]);

    // Функция для расчета значения из позиции мыши
    const getValueFromPosition = useCallback((clientX, rect) => {
        let x = clientX - rect.left;
        x = Math.max(TRACK_START, Math.min(x, TRACK_END));
        const percent = (x - TRACK_START) / SLIDER_WIDTH;
        let newValue = MIN_DEVICES + percent * (MAX_DEVICES - MIN_DEVICES);
        return Math.max(MIN_DEVICES, Math.min(MAX_DEVICES, newValue));
    }, [MIN_DEVICES, MAX_DEVICES, TRACK_START, TRACK_END, SLIDER_WIDTH]);

    // Плавная анимация обновления позиции
    const animateToValue = useCallback((targetValue, duration = 200) => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        const startValue = smoothDevices;
        const startTime = performance.now();
        const distance = targetValue - startValue;

        if (Math.abs(distance) < 0.01) {
            setSmoothDevices(targetValue);
            updateSliderPosition(targetValue);
            return;
        }

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(1, elapsed / duration);
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            const currentValue = startValue + distance * easeProgress;

            setSmoothDevices(currentValue);
            updateSliderPosition(currentValue);

            const roundedValue = Math.round(currentValue);
            if (roundedValue !== lastDeviceValue.current) {
                triggerDeviceAnimation(roundedValue);
            }

            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            } else {
                setSmoothDevices(targetValue);
                updateSliderPosition(targetValue);
                animationRef.current = null;
            }
        };

        animationRef.current = requestAnimationFrame(animate);
    }, [smoothDevices, updateSliderPosition, triggerDeviceAnimation]);

    // Обработчики событий слайдера
    const handleDragStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);

        if (thumbRef.current) {
            thumbRef.current.classList.add('dragging');
        }
    }, []);

    const handleDragMove = useCallback((e) => {
        if (!isDragging || !sliderRef.current) return;

        e.preventDefault();

        const clientX = e.clientX ?? e.touches?.[0]?.clientX;
        if (!clientX) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const rawValue = getValueFromPosition(clientX, rect);
        const roundedValue = Math.round(rawValue);

        setSmoothDevices(rawValue);
        updateSliderPosition(rawValue);

        if (roundedValue !== lastDeviceValue.current) {
            triggerDeviceAnimation(roundedValue);
            setDevices(roundedValue);
        }
    }, [isDragging, getValueFromPosition, updateSliderPosition, triggerDeviceAnimation]);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);

        if (thumbRef.current) {
            thumbRef.current.classList.remove('dragging');
        }

        const roundedValue = Math.round(smoothDevices);
        const finalValue = Math.max(MIN_DEVICES, Math.min(MAX_DEVICES, roundedValue));

        if (finalValue !== devices) {
            animateToValue(finalValue, 150);
            setDevices(finalValue);
        } else {
            animateToValue(finalValue, 150);
        }

        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleDragMove);
        document.removeEventListener('touchend', handleDragEnd);
    }, [isDragging, smoothDevices, devices, MIN_DEVICES, MAX_DEVICES, animateToValue]);

    const handleTrackClick = useCallback((e) => {
        if (e.target === thumbRef.current || thumbRef.current?.contains(e.target)) {
            return;
        }

        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const clientX = e.clientX ?? e.touches?.[0]?.clientX;
        if (!clientX) return;

        const rawValue = getValueFromPosition(clientX, rect);
        const roundedValue = Math.round(rawValue);

        if (roundedValue !== devices && roundedValue >= MIN_DEVICES) {
            setDevices(roundedValue);
            triggerDeviceAnimation(roundedValue);
            animateToValue(roundedValue, 200);
        }
    }, [devices, MIN_DEVICES, getValueFromPosition, animateToValue, triggerDeviceAnimation]);

    // Добавляем глобальные обработчики при начале перетаскивания
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
            document.addEventListener('touchmove', handleDragMove, { passive: false });
            document.addEventListener('touchend', handleDragEnd);

            return () => {
                document.removeEventListener('mousemove', handleDragMove);
                document.removeEventListener('mouseup', handleDragEnd);
                document.removeEventListener('touchmove', handleDragMove);
                document.removeEventListener('touchend', handleDragEnd);
            };
        }
    }, [isDragging, handleDragMove, handleDragEnd]);

    // Установка начальной позиции слайдера
    useEffect(() => {
        if (sliderRef.current && thumbRef.current) {
            updateSliderPosition(devices);
            setSmoothDevices(devices);
            setDisplayDevices(devices);
            lastDeviceValue.current = devices;
        }
    }, [devices, updateSliderPosition]);

    // Вычисляем цены для отображения
    const totalPrice = getTariffPrice(devices, period);
    const regularPrice = getRegularPrice(devices, period);
    const discountPercent = getDiscountPercent(period);
    const discountAmount = regularPrice - totalPrice;
    const hasDiscount = discountPercent > 0;



    const currentDisplayValue = Math.round(smoothDevices);
    const showValue = isDragging ? currentDisplayValue : displayDevices;

    // Обработчик нажатия на кнопку покупки
    const handlePurchaseClick = async () => {
        if (hapticFeedback.impactOccurred.isAvailable()) {
            hapticFeedback.impactOccurred('heavy');
        }

        // Получаем баланс
        const balance = await fetchBalance();
        setCurrentBalance(balance);

        // Проверяем, хватает ли баланса
        if (balance < totalPrice) {
            // Недостаточно средств - показываем шторку
            setRequiredAmount(totalPrice - balance);
            setShowInsufficientBalanceSheet(true);
            return;
        }

        // Если это продление существующего ключа, показываем шторку подтверждения
        if (renewData) {
            setShowPurchaseSheet(true);
            return;
        }

        // Проверяем наличие активных ключей
        setIsCheckingKeys(true);
        try {
            const count = await checkActiveKeys();
            setActiveKeysCount(count);

            if (count > 0) {
                // Есть активные ключи — показываем предупреждение
                setShowWarningSheet(true);
            } else {
                // Нет активных ключей — показываем шторку подтверждения
                setShowPurchaseSheet(true);
            }
        } catch (error) {
            console.error('Ошибка при проверке ключей:', error);
            setShowPurchaseSheet(true);
        } finally {
            setIsCheckingKeys(false);
        }
    };

    // Подтверждение покупки после предупреждения
    const handleWarningConfirm = async () => {
        setShowWarningSheet(false);
        setShowPurchaseSheet(true);
    };

    // Отмена предупреждения
    const handleWarningCancel = () => {
        setShowWarningSheet(false);
    };

    // Финальное подтверждение покупки
    const handleConfirmPurchase = async () => {
        setShowPurchaseSheet(false);
        setLoading(true);
        try {
            await onPurchase({
                period,
                devices,
                whiteList: false,
                totalPrice,
                userData,
                renewUuid: renewData?.uuid
            });
        } catch (error) {
            console.error('Ошибка при покупке:', error);
        } finally {
            setLoading(false);
        }
    };

    // Отмена покупки
    const handleCancelPurchase = () => {
        setShowPurchaseSheet(false);
    };

    // Закрытие шторки недостаточного баланса
    const handleCloseInsufficientBalance = () => {
        setShowInsufficientBalanceSheet(false);
    };

    // Переход в кошелек для пополнения
    const handleGoToWallet = () => {
        setShowInsufficientBalanceSheet(false);
        if (onBack) {
            onBack();
        }
        if(onGoWallet){
            onGoWallet();
        }
    };

    const { isTelegram } = useTelegram();
// ==================== ХАЙЛАЙТ ====================
    useEffect(() => {
        if (isTelegram && backButton) {
            try {
                backButton.mount();
                backButton.show();
                backButton.onClick(() => {
                    onBack();
                    backButton.hide();
                });
            } catch (error) {
                console.error('Ошибка инициализации backButton:', error);
            }
        }

        return () => {
            if (isTelegram && backButton) {
                try {
                    backButton.hide();
                } catch (error) {
                    // игнорируем
                }
            }
        };
    }, [onBack, isTelegram]);

    const newBalance = currentBalance - totalPrice;

    return (
        <>
            {!isTelegram && (

                <button
                    onClick={onBack}
                    className="web-back-button-left"
                >
                    ← Назад
                </button>

            )}
        <div className="purchase-page-content">
            <div className="v1_91">
                <div className="v1_92">
                    <PeriodSelector
                        selectedPeriod={period}
                        onPeriodChange={setPeriod}
                    />

                    {/* Плавный слайдер устройств с анимацией счетчика */}
                    <div className="v1_102">
                        <div className="v1_103">
                            <div className="v1_104">
                                <div className="v1_105">
                                    <span className="v1_106">Количество устройств</span>
                                    <span
                                        className={`v1_107 ${isAnimating ? 'device-count-animate' : ''} ${animationDirection === 'increase' ? 'animate-increase' : ''} ${animationDirection === 'decrease' ? 'animate-decrease' : ''}`}
                                        key={showValue}
                                    >
                                        {showValue}
                                    </span>
                                </div>
                                <div
                                    className="v1_108"
                                    ref={sliderRef}
                                    onClick={handleTrackClick}
                                    onTouchStart={handleTrackClick}
                                >
                                    <div className="v1_110"></div>
                                    <div
                                        className="v1_109 slider-fill-smooth"
                                        ref={fillRef}
                                    ></div>
                                    <div
                                        className="v1_111 slider-thumb-smooth"
                                        ref={thumbRef}
                                        onMouseDown={handleDragStart}
                                        onTouchStart={handleDragStart}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>



                <PricePanel     totalPrice={totalPrice}
                                period={period}
                                regularPrice={regularPrice}
                                discountPercent={discountPercent} />
                <PurchaseButton loading={loading} onClick={handlePurchaseClick} />
            </div>
            </div>

            {/* Шторка предупреждения об активных ключах */}
            <ConfirmationSheet
                isVisible={showWarningSheet}
                onConfirm={handleWarningConfirm}
                onCancel={handleWarningCancel}
            />

            {/* Шторка подтверждения покупки */}
            <PurchaseConfirmationSheet
                isVisible={showPurchaseSheet}
                onConfirm={handleConfirmPurchase}
                onCancel={handleCancelPurchase}
                period={period}
                devices={devices}
                totalPrice={totalPrice}
                currentBalance={currentBalance}
                newBalance={newBalance}
                regularPrice={regularPrice}
                discountPercent={discountPercent}
                hasDiscount={hasDiscount}
            />

            {/* Шторка недостаточного баланса */}
            <InsufficientBalanceSheet
                isVisible={showInsufficientBalanceSheet}
                onClose={handleCloseInsufficientBalance}
                requiredAmount={requiredAmount}
                currentBalance={currentBalance}
                onGoWallet={handleGoToWallet}
            />
        </div>
            </>
    );
}
